import type { ComponentPropsWithRef } from 'react';
import { Slot } from '@radix-ui/react-slot';

import { cn } from '@/lib/utils';
import { buttonVariants, type ButtonVariantsProps } from './button.variants';

type PurchasePricedProps = {
    variant: 'purchase-priced';
    /** Price displayed right-aligned in the purchase-priced variant. */
    price: string;
    /** Original price shown with strikethrough before the current price. */
    originalPrice?: string;
};

type OtherVariantProps = {
    variant?: Exclude<ButtonVariantsProps['variant'], 'purchase-priced'>;
    price?: never;
    originalPrice?: never;
};

export type ButtonProps = ComponentPropsWithRef<'button'> &
    Omit<ButtonVariantsProps, 'variant'> & {
        asChild?: boolean;
        /** Keeps the matches variant in its pressed/selected visual state (sets aria-pressed). */
        selected?: boolean;
    } & (PurchasePricedProps | OtherVariantProps);

export const Button = ({
    className,
    variant,
    size,
    linkHoverUnderline,
    asChild = false,
    selected,
    price,
    originalPrice,
    ref,
    children,
    ...props
}: ButtonProps) => {
    const Comp = asChild ? Slot : 'button';
    const isPurchasePriced = variant === 'purchase-priced';

    return (
        <Comp
            data-slot="button"
            data-variant={variant ?? 'default'}
            data-size={size ?? 'default'}
            className={cn(buttonVariants({ variant, size, className, linkHoverUnderline }))}
            aria-pressed={selected}
            ref={ref as never}
            {...props}>
            {isPurchasePriced && price ? (
                <>
                    <span
                        data-slot="purchase-label"
                        className="flex flex-1 items-center justify-center text-[15px] tracking-[1.95px]">
                        {children}
                    </span>
                    <span
                        data-slot="purchase-divider"
                        aria-hidden="true"
                        className="h-[18px] w-px shrink-0 bg-white/60"
                    />
                    <span
                        data-slot="purchase-price"
                        className="flex shrink-0 items-center gap-2 text-[15px] tracking-[1.95px]">
                        {originalPrice && <span className="line-through opacity-60">{originalPrice}</span>}
                        <span>{price}</span>
                    </span>
                </>
            ) : (
                children
            )}
        </Comp>
    );
};
